/* src/utils/formTemplates.js
 * Pre-built form templates with styling options
 */

// Layout options for templates
export const LAYOUT_OPTIONS = [
  {
    id: 'modern',
    name: 'Modern',
    description: 'Clean, modern design with rounded corners',
    styles: {
      layout: 'single-column',
      spacing: 'comfortable',
      fieldStyle: 'rounded',
      buttonStyle: 'rounded-full',
    },
  },
  {
    id: 'multi-step',
    name: 'Multi-Step',
    description: 'Break form into multiple steps with progress indicator',
    styles: {
      layout: 'multi-step',
      spacing: 'comfortable',
      fieldStyle: 'rounded',
      buttonStyle: 'rounded',
      steps: 3, // Default 3 steps
      showProgress: true,
    },
  },
  {
    id: 'accordion',
    name: 'Accordion',
    description: 'Collapsible sections for organized form sections',
    styles: {
      layout: 'accordion',
      spacing: 'comfortable',
      fieldStyle: 'rounded',
      buttonStyle: 'rounded',
      collapsible: true,
    },
  },
  {
    id: 'tabbed',
    name: 'Tabbed',
    description: 'Organize fields into tabs for easy navigation',
    styles: {
      layout: 'tabbed',
      spacing: 'comfortable',
      fieldStyle: 'rounded',
      buttonStyle: 'rounded',
      tabs: ['Personal', 'Business', 'Preferences'],
    },
  },
  {
    id: 'two-column',
    name: 'Two Column',
    description: 'Fields arranged in two columns (desktop)',
    styles: {
      layout: 'two-column',
      spacing: 'comfortable',
      fieldStyle: 'rounded',
      buttonStyle: 'rounded',
    },
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Minimalist design with subtle borders',
    styles: {
      layout: 'single-column',
      spacing: 'compact',
      fieldStyle: 'minimal',
      buttonStyle: 'minimal',
    },
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional form layout with clear sections',
    styles: {
      layout: 'single-column',
      spacing: 'standard',
      fieldStyle: 'square',
      buttonStyle: 'square',
    },
  },
];

export const FORM_TEMPLATES = [
  {
    id: 'contact',
    name: 'Contact Form',
    description: 'Simple contact form with name, email, and message',
    icon: '📧',
    category: 'general',
    fields: [
      {
        id: 'name',
        type: 'text',
        label: 'Full Name',
        name: 'name',
        placeholder: '',
        required: true,
        order: 0,
      },
      {
        id: 'email',
        type: 'email',
        label: 'Email Address',
        name: 'email',
        placeholder: '',
        required: true,
        order: 1,
      },
      {
        id: 'phone',
        type: 'phone',
        label: 'Phone Number',
        name: 'phone',
        placeholder: '',
        required: false,
        order: 2,
      },
      {
        id: 'message',
        type: 'textarea',
        label: 'Message',
        name: 'message',
        placeholder: 'How can we help?',
        required: true,
        order: 3,
      },
    ],
    defaultTheme: {
      primaryColor: '#1e40af',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#1e40af',
      buttonTextColor: '#ffffff',
    },
  },
  {
    id: 'lead-gen',
    name: 'Lead Generation',
    description: 'Capture leads with company info',
    icon: '🎯',
    category: 'general',
    fields: [
      {
        id: 'firstName',
        type: 'text',
        label: 'First Name',
        name: 'firstName',
        placeholder: '',
        required: true,
        order: 0,
      },
      {
        id: 'lastName',
        type: 'text',
        label: 'Last Name',
        name: 'lastName',
        placeholder: '',
        required: true,
        order: 1,
      },
      {
        id: 'email',
        type: 'email',
        label: 'Business Email',
        name: 'email',
        placeholder: '',
        required: true,
        order: 2,
      },
      {
        id: 'company',
        type: 'text',
        label: 'Company Name',
        name: 'company',
        placeholder: '',
        required: true,
        order: 3,
      },
      {
        id: 'phone',
        type: 'phone',
        label: 'Phone Number',
        name: 'phone',
        placeholder: '',
        required: false,
        order: 4,
      },
      {
        id: 'interest',
        type: 'select',
        label: 'I\'m interested in',
        name: 'interest',
        required: true,
        options: ['Product Demo', 'Pricing', 'Partnership', 'Other'],
        order: 5,
      },
    ],
    defaultTheme: {
      primaryColor: '#28a745',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#28a745',
      buttonTextColor: '#ffffff',
    },
  },
  {
    id: 'survey',
    name: 'Survey Form',
    description: 'Collect feedback and opinions',
    icon: '📊',
    category: 'general',
    fields: [
      {
        id: 'satisfaction',
        type: 'radio',
        label: 'How satisfied are you?',
        name: 'satisfaction',
        required: true,
        options: ['Very Satisfied', 'Satisfied', 'Neutral', 'Dissatisfied', 'Very Dissatisfied'],
        order: 0,
      },
      {
        id: 'recommend',
        type: 'radio',
        label: 'Would you recommend us?',
        name: 'recommend',
        required: true,
        options: ['Definitely', 'Probably', 'Maybe', 'Probably Not', 'Definitely Not'],
        order: 1,
      },
      {
        id: 'feedback',
        type: 'textarea',
        label: 'Additional Comments',
        name: 'feedback',
        placeholder: 'Your comments (optional)',
        required: false,
        order: 2,
      },
      {
        id: 'email',
        type: 'email',
        label: 'Email (optional)',
        name: 'email',
        placeholder: '',
        required: false,
        order: 3,
      },
    ],
    defaultTheme: {
      primaryColor: '#6f42c1',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#6f42c1',
      buttonTextColor: '#ffffff',
    },
  },
  {
    id: 'event-registration',
    name: 'Event Registration',
    description: 'Register for events and webinars',
    icon: '🎫',
    category: 'general',
    fields: [
      {
        id: 'name',
        type: 'text',
        label: 'Full Name',
        name: 'name',
        placeholder: '',
        required: true,
        order: 0,
      },
      {
        id: 'email',
        type: 'email',
        label: 'Email Address',
        name: 'email',
        placeholder: '',
        required: true,
        order: 1,
      },
      {
        id: 'company',
        type: 'text',
        label: 'Company/Organization',
        name: 'company',
        placeholder: '',
        required: false,
        order: 2,
      },
      {
        id: 'dietary',
        type: 'select',
        label: 'Dietary Restrictions',
        name: 'dietary',
        required: false,
        options: ['None', 'Vegetarian', 'Vegan', 'Gluten-Free', 'Other'],
        order: 3,
      },
      {
        id: 'notes',
        type: 'textarea',
        label: 'Special Requests',
        name: 'notes',
        placeholder: 'Special requests (optional)',
        required: false,
        order: 4,
      },
    ],
    defaultTheme: {
      primaryColor: '#dc3545',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#dc3545',
      buttonTextColor: '#ffffff',
    },
  },
  {
    id: 'quote-request',
    name: 'Quote Request',
    description: 'Get custom quotes from potential customers',
    icon: '💰',
    category: 'general',
    fields: [
      {
        id: 'name',
        type: 'text',
        label: 'Contact Name',
        name: 'name',
        placeholder: '',
        required: true,
        order: 0,
      },
      {
        id: 'email',
        type: 'email',
        label: 'Email Address',
        name: 'email',
        placeholder: '',
        required: true,
        order: 1,
      },
      {
        id: 'phone',
        type: 'phone',
        label: 'Phone Number',
        name: 'phone',
        placeholder: '',
        required: true,
        order: 2,
      },
      {
        id: 'project-type',
        type: 'select',
        label: 'Project Type',
        name: 'projectType',
        required: true,
        options: ['Website', 'Mobile App', 'Branding', 'Marketing', 'Other'],
        order: 3,
      },
      {
        id: 'budget',
        type: 'select',
        label: 'Budget Range',
        name: 'budget',
        required: true,
        options: ['$1,000 - $5,000', '$5,000 - $10,000', '$10,000 - $25,000', '$25,000+'],
        order: 4,
      },
      {
        id: 'details',
        type: 'textarea',
        label: 'Project Details',
        name: 'details',
        placeholder: 'Describe your project',
        required: true,
        order: 5,
      },
    ],
    defaultTheme: {
      primaryColor: '#ffc107',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#ffc107',
      buttonTextColor: '#000000',
    },
  },
  {
    id: 'feedback',
    name: 'Feedback Form',
    description: 'Collect user feedback and suggestions',
    icon: '💬',
    category: 'general',
    fields: [
      {
        id: 'rating',
        type: 'radio',
        label: 'Overall Rating',
        name: 'rating',
        required: true,
        options: ['5 - Excellent', '4 - Good', '3 - Average', '2 - Poor', '1 - Very Poor'],
        order: 0,
      },
      {
        id: 'category',
        type: 'select',
        label: 'Category',
        name: 'category',
        required: true,
        options: ['Product', 'Service', 'Support', 'Website', 'Other'],
        order: 1,
      },
      {
        id: 'feedback',
        type: 'textarea',
        label: 'Your Feedback',
        name: 'feedback',
        placeholder: 'Your feedback',
        required: true,
        order: 2,
      },
      {
        id: 'email',
        type: 'email',
        label: 'Email (optional)',
        name: 'email',
        placeholder: '',
        required: false,
        order: 3,
      },
    ],
    defaultTheme: {
      primaryColor: '#17a2b8',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#17a2b8',
      buttonTextColor: '#ffffff',
    },
  },

  // ---------------------------------------------------------------------
  // Industry templates - niche intake forms with a starter AI knowledge
  // base and follow-up defaults seeded on creation (see formTemplates
  // helpers below and FormBuilderList.createNewForm).
  // ---------------------------------------------------------------------
  {
    id: 'personal-injury',
    name: 'Personal Injury Intake',
    description: 'Qualify accident and injury leads for a law firm',
    icon: '⚖️',
    category: 'industry',
    defaultLayout: 'multi-step',
    fields: [
      { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: '', required: true, order: 0 },
      { id: 'phone', type: 'phone', label: 'Phone Number', name: 'phone', placeholder: '', required: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: '', required: true, order: 2 },
      { id: 'incidentType', type: 'select', label: 'Type of Incident', name: 'incidentType', required: true, options: ['Auto accident', 'Slip and fall', 'Work injury', 'Medical', 'Dog bite', 'Other'], order: 3 },
      { id: 'incidentDate', type: 'date', label: 'Date of Incident', name: 'incidentDate', required: true, order: 4 },
      { id: 'wasInjured', type: 'radio', label: 'Were you injured?', name: 'wasInjured', required: true, options: ['Yes', 'No'], order: 5 },
      { id: 'sawDoctor', type: 'radio', label: 'Have you seen a doctor or other medical provider?', name: 'sawDoctor', required: true, options: ['Yes', 'No'], order: 6 },
      { id: 'hasAttorney', type: 'radio', label: 'Are you already represented by an attorney?', name: 'hasAttorney', required: true, options: ['Yes', 'No'], order: 7 },
      { id: 'description', type: 'textarea', label: 'Briefly describe what happened', name: 'description', placeholder: '', required: true, order: 8 },
    ],
    defaultTheme: {
      primaryColor: '#1e3a8a',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#1e3a8a',
      buttonTextColor: '#ffffff',
    },
    aiKnowledgeBase: `You are the intake assistant for a personal injury law firm. Your job is to warmly qualify incoming leads before a human takes over.

When a new lead comes in:
- Confirm the type of incident (auto accident, slip and fall, work injury, medical, dog bite, or other) and when it happened.
- Ask whether they were injured and whether they have seen a doctor or other medical provider.
- Ask whether they are already represented by another attorney. If they are, thank them, explain the firm cannot get involved while they have existing representation, and end the conversation politely.
- Be empathetic. Many leads are dealing with pain, stress, or financial pressure.
- Never give legal advice, never estimate what a case is worth, and never guarantee an outcome. Leave all legal opinions to the attorney.
- Do not ask for a Social Security number, date of birth, or detailed medical history. A simple yes or no on injury and treatment is enough for intake.
- If the lead asks about scheduling a consultation, offer to have someone from the firm follow up, and only share a booking link if one has been set up for this form.
- Keep replies short, clear, and reassuring. The goal is to gather enough detail for a quick callback, not to resolve the case by email.`,
    followUpDefaults: { followUpEnabled: true, followUpDelayDays: 1, maxFollowUps: 3 },
  },
  {
    id: 'plumbing',
    name: 'Plumbing Service Request',
    description: 'Qualify plumbing service requests, including emergencies',
    icon: '🔧',
    category: 'industry',
    fields: [
      { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: '', required: true, order: 0 },
      { id: 'phone', type: 'phone', label: 'Phone Number', name: 'phone', placeholder: '', required: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: '', required: true, order: 2 },
      { id: 'serviceType', type: 'select', label: 'Service Type', name: 'serviceType', required: true, options: ['Repair', 'Installation', 'Emergency', 'Drain cleaning', 'Water heater', 'Inspection'], order: 3 },
      { id: 'isEmergency', type: 'radio', label: 'Is this an emergency?', name: 'isEmergency', required: true, options: ['Yes', 'No'], order: 4 },
      { id: 'propertyType', type: 'select', label: 'Property Type', name: 'propertyType', required: true, options: ['Residential', 'Commercial'], order: 5 },
      { id: 'serviceAddress', type: 'text', label: 'Service Address or ZIP Code', name: 'serviceAddress', placeholder: '', required: true, order: 6 },
      { id: 'issueDescription', type: 'textarea', label: 'Describe the Issue', name: 'issueDescription', placeholder: '', required: true, order: 7 },
    ],
    defaultTheme: {
      primaryColor: '#0ea5e9',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#0ea5e9',
      buttonTextColor: '#ffffff',
    },
    aiKnowledgeBase: `You are the intake assistant for a plumbing company. Your job is to qualify incoming service requests before a human follows up.

When a new lead comes in:
- Confirm the type of service needed (repair, installation, emergency, drain cleaning, water heater, or inspection).
- Ask if this is an emergency (active leak, no water, sewage backup) so urgent jobs get flagged for a fast callback.
- Confirm whether the property is residential or commercial, and get the service address or ZIP code.
- Ask a short follow-up question about the issue if the description is vague, for example how long it has been happening or where it is located.
- Be friendly and practical. Homeowners contacting a plumber are often dealing with an inconvenient or stressful problem.
- Do not quote exact prices unless pricing information has been provided in this knowledge base. If unsure, say a technician will confirm pricing after understanding the job.
- If the lead asks about scheduling, offer to have someone follow up, and only share a booking link if one has been set up for this form.
- Keep replies short and to the point.`,
    followUpDefaults: { followUpEnabled: true, followUpDelayDays: 1, maxFollowUps: 2 },
  },
  {
    id: 'hvac',
    name: 'HVAC Service Request',
    description: 'Qualify heating and cooling service requests',
    icon: '🌡️',
    category: 'industry',
    fields: [
      { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: '', required: true, order: 0 },
      { id: 'phone', type: 'phone', label: 'Phone Number', name: 'phone', placeholder: '', required: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: '', required: true, order: 2 },
      { id: 'serviceType', type: 'select', label: 'Service Type', name: 'serviceType', required: true, options: ['Repair', 'Installation', 'Maintenance', 'Emergency'], order: 3 },
      { id: 'systemType', type: 'select', label: 'System Type', name: 'systemType', required: true, options: ['Central AC', 'Furnace', 'Heat pump', 'Ductless mini-split', 'Not sure'], order: 4 },
      { id: 'isEmergency', type: 'radio', label: 'Is this an emergency?', name: 'isEmergency', required: true, options: ['Yes', 'No'], order: 5 },
      { id: 'propertyType', type: 'select', label: 'Property Type', name: 'propertyType', required: true, options: ['Residential', 'Commercial'], order: 6 },
      { id: 'serviceAddress', type: 'text', label: 'Service Address or ZIP Code', name: 'serviceAddress', placeholder: '', required: true, order: 7 },
      { id: 'issueDescription', type: 'textarea', label: 'Describe the Problem', name: 'issueDescription', placeholder: '', required: true, order: 8 },
    ],
    defaultTheme: {
      primaryColor: '#f97316',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#f97316',
      buttonTextColor: '#ffffff',
    },
    aiKnowledgeBase: `You are the intake assistant for an HVAC company. Your job is to qualify incoming service requests before a human follows up.

When a new lead comes in:
- Confirm the type of service needed (repair, installation, maintenance, or emergency) and the system type if known (central AC, furnace, heat pump, ductless mini-split).
- Ask if this is an emergency (no heat, no AC in extreme weather, gas smell) so urgent jobs get flagged for a fast callback.
- Confirm whether the property is residential or commercial, and get the service address or ZIP code.
- Ask a short follow-up question about the problem if the description is vague.
- Be friendly and practical. Comfort issues can be urgent, especially in extreme weather.
- Do not quote exact prices unless pricing information has been provided in this knowledge base.
- If the lead asks about scheduling, offer to have someone follow up, and only share a booking link if one has been set up for this form.
- Keep replies short and to the point.`,
    followUpDefaults: { followUpEnabled: true, followUpDelayDays: 1, maxFollowUps: 2 },
  },
  {
    id: 'electrical',
    name: 'Electrical Service Request',
    description: 'Qualify electrical service requests, including emergencies',
    icon: '⚡',
    category: 'industry',
    fields: [
      { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: '', required: true, order: 0 },
      { id: 'phone', type: 'phone', label: 'Phone Number', name: 'phone', placeholder: '', required: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: '', required: true, order: 2 },
      { id: 'serviceType', type: 'select', label: 'Service Type', name: 'serviceType', required: true, options: ['Repair', 'Installation', 'Panel upgrade', 'Inspection', 'Emergency'], order: 3 },
      { id: 'isEmergency', type: 'radio', label: 'Is this an emergency?', name: 'isEmergency', required: true, options: ['Yes', 'No'], order: 4 },
      { id: 'propertyType', type: 'select', label: 'Property Type', name: 'propertyType', required: true, options: ['Residential', 'Commercial'], order: 5 },
      { id: 'serviceAddress', type: 'text', label: 'Service Address or ZIP Code', name: 'serviceAddress', placeholder: '', required: true, order: 6 },
      { id: 'jobDescription', type: 'textarea', label: 'Describe the Job', name: 'jobDescription', placeholder: '', required: true, order: 7 },
    ],
    defaultTheme: {
      primaryColor: '#eab308',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#eab308',
      buttonTextColor: '#000000',
    },
    aiKnowledgeBase: `You are the intake assistant for an electrical services company. Your job is to qualify incoming service requests before a human follows up.

When a new lead comes in:
- Confirm the type of service needed (repair, installation, panel upgrade, inspection, or emergency).
- Ask if this is an emergency (sparking, burning smell, exposed wiring, power outage) so urgent jobs get flagged for a fast callback and the lead is told to treat any immediate safety hazard seriously.
- Confirm whether the property is residential or commercial, and get the service address or ZIP code.
- Ask a short follow-up question about the job if the description is vague.
- Be friendly, clear, and safety conscious. If a lead describes something that sounds dangerous, encourage them to shut off power at the breaker if they can do so safely and to call in urgently.
- Do not quote exact prices unless pricing information has been provided in this knowledge base.
- If the lead asks about scheduling, offer to have someone follow up, and only share a booking link if one has been set up for this form.
- Keep replies short and to the point.`,
    followUpDefaults: { followUpEnabled: true, followUpDelayDays: 1, maxFollowUps: 2 },
  },
  {
    id: 'roofing',
    name: 'Roofing Estimate Request',
    description: 'Qualify roofing leads, including active leaks',
    icon: '🏠',
    category: 'industry',
    fields: [
      { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: '', required: true, order: 0 },
      { id: 'phone', type: 'phone', label: 'Phone Number', name: 'phone', placeholder: '', required: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: '', required: true, order: 2 },
      { id: 'serviceType', type: 'select', label: 'Service Type', name: 'serviceType', required: true, options: ['Repair', 'Full replacement', 'Inspection', 'Storm damage', 'Gutters'], order: 3 },
      { id: 'isLeaking', type: 'radio', label: 'Is your roof currently leaking?', name: 'isLeaking', required: true, options: ['Yes', 'No'], order: 4 },
      { id: 'propertyType', type: 'select', label: 'Property Type', name: 'propertyType', required: true, options: ['Residential', 'Commercial'], order: 5 },
      { id: 'propertyAddress', type: 'text', label: 'Property Address', name: 'propertyAddress', placeholder: '', required: true, order: 6 },
      { id: 'workDescription', type: 'textarea', label: 'Describe the Work Needed', name: 'workDescription', placeholder: '', required: true, order: 7 },
    ],
    defaultTheme: {
      primaryColor: '#78716c',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#78716c',
      buttonTextColor: '#ffffff',
    },
    aiKnowledgeBase: `You are the intake assistant for a roofing company. Your job is to qualify incoming leads before a human follows up.

When a new lead comes in:
- Confirm the type of service needed (repair, full replacement, inspection, storm damage, or gutters).
- Ask if the roof is currently leaking so active leaks get flagged for a fast callback.
- Confirm whether the property is residential or commercial, and get the property address.
- Ask a short follow-up question about the work needed if the description is vague, for example the age of the roof or when the damage was noticed.
- Be friendly and practical. Roofing leads are often stressed about active damage or an upcoming insurance claim.
- Do not quote exact prices unless pricing information has been provided in this knowledge base.
- If the lead mentions an insurance claim, let them know the team can help document the damage, but do not give insurance advice.
- If the lead asks about scheduling, offer to have someone follow up, and only share a booking link if one has been set up for this form.
- Keep replies short and to the point.`,
    followUpDefaults: { followUpEnabled: true, followUpDelayDays: 1, maxFollowUps: 2 },
  },
  {
    id: 'dental-medspa',
    name: 'Dental and Med Spa Consultation',
    description: 'Qualify new patient and treatment inquiries',
    icon: '🦷',
    category: 'industry',
    fields: [
      { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: '', required: true, order: 0 },
      { id: 'phone', type: 'phone', label: 'Phone Number', name: 'phone', placeholder: '', required: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: '', required: true, order: 2 },
      { id: 'serviceInterest', type: 'select', label: 'Service Interest', name: 'serviceInterest', required: true, options: ['Cleaning and checkup', 'Cosmetic dentistry', 'Implants', 'Invisalign or braces', 'Med spa treatment', 'Other'], order: 3 },
      { id: 'isNewPatient', type: 'radio', label: 'Are you a new patient?', name: 'isNewPatient', required: true, options: ['Yes', 'No'], order: 4 },
      { id: 'preferredTime', type: 'select', label: 'Preferred Appointment Time', name: 'preferredTime', required: true, options: ['Morning', 'Afternoon', 'Evening'], order: 5 },
      { id: 'additionalNotes', type: 'textarea', label: 'Anything else we should know?', name: 'additionalNotes', placeholder: '', required: false, order: 6 },
    ],
    defaultTheme: {
      primaryColor: '#ec4899',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#ec4899',
      buttonTextColor: '#ffffff',
    },
    aiKnowledgeBase: `You are the intake assistant for a dental and med spa practice. Your job is to warmly qualify incoming leads before a human follows up.

When a new lead comes in:
- Confirm what they are interested in (cleaning and checkup, cosmetic dentistry, implants, Invisalign or braces, med spa treatment, or other).
- Ask if they are a new patient.
- Ask about their preferred appointment time (morning, afternoon, or evening) to help scheduling.
- Be warm and welcoming. Many leads are nervous about dental or cosmetic procedures.
- Do not give medical or treatment advice, do not diagnose, and do not quote exact pricing unless it has been provided in this knowledge base. Leave clinical guidance to the provider.
- If the lead asks about scheduling, offer to have someone follow up, and only share a booking link if one has been set up for this form.
- Keep replies short, friendly, and reassuring.`,
    followUpDefaults: { followUpEnabled: true, followUpDelayDays: 2, maxFollowUps: 2 },
  },
  {
    id: 'real-estate',
    name: 'Real Estate Inquiry',
    description: 'Qualify buyer, seller, and renter leads',
    icon: '🏡',
    category: 'industry',
    fields: [
      { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: '', required: true, order: 0 },
      { id: 'phone', type: 'phone', label: 'Phone Number', name: 'phone', placeholder: '', required: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: '', required: true, order: 2 },
      { id: 'buyingOrSelling', type: 'select', label: 'Are you buying or selling?', name: 'buyingOrSelling', required: true, options: ['Buying', 'Selling', 'Both', 'Renting'], order: 3 },
      { id: 'areaOfInterest', type: 'text', label: 'Area or Neighborhood of Interest', name: 'areaOfInterest', placeholder: '', required: true, order: 4 },
      { id: 'timeline', type: 'select', label: 'Timeline', name: 'timeline', required: true, options: ['As soon as possible', '1 to 3 months', '3 to 6 months', '6+ months', 'Just browsing'], order: 5 },
      { id: 'budgetRange', type: 'select', label: 'Budget Range', name: 'budgetRange', required: true, options: ['Under $250k', '$250k to $500k', '$500k to $750k', '$750k to $1M', 'Over $1M'], order: 6 },
      { id: 'additionalNotes', type: 'textarea', label: 'Anything Else?', name: 'additionalNotes', placeholder: '', required: false, order: 7 },
    ],
    defaultTheme: {
      primaryColor: '#16a34a',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#16a34a',
      buttonTextColor: '#ffffff',
    },
    aiKnowledgeBase: `You are the intake assistant for a real estate agent or team. Your job is to qualify incoming leads before a human follows up.

When a new lead comes in:
- Confirm whether they are buying, selling, renting, or both.
- Ask about the area or neighborhood they are interested in.
- Ask about their timeline (as soon as possible, one to three months, three to six months, six or more months, or just browsing).
- Ask about their budget range to help match them with the right listings.
- Be friendly and consultative. Buyers and sellers often have a lot of questions early on.
- Do not give legal, tax, or financial advice, and do not quote a specific property value. Leave pricing opinions and valuations to the agent.
- If the lead asks about scheduling a showing or consultation, offer to have someone follow up, and only share a booking link if one has been set up for this form.
- Keep replies short and helpful.`,
    followUpDefaults: { followUpEnabled: true, followUpDelayDays: 2, maxFollowUps: 2 },
  },
  {
    id: 'auto-repair',
    name: 'Auto Repair Request',
    description: 'Qualify vehicle repair and service leads',
    icon: '🚗',
    category: 'industry',
    fields: [
      { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: '', required: true, order: 0 },
      { id: 'phone', type: 'phone', label: 'Phone Number', name: 'phone', placeholder: '', required: true, order: 1 },
      { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: '', required: true, order: 2 },
      { id: 'vehicleInfo', type: 'text', label: 'Vehicle Year, Make, and Model', name: 'vehicleInfo', placeholder: '', required: true, order: 3 },
      { id: 'serviceNeeded', type: 'select', label: 'Service Needed', name: 'serviceNeeded', required: true, options: ['Diagnostics', 'Brakes', 'Oil change and maintenance', 'Engine', 'Transmission', 'Tires', 'Other'], order: 4 },
      { id: 'isDrivable', type: 'radio', label: 'Is the vehicle drivable?', name: 'isDrivable', required: true, options: ['Yes', 'No'], order: 5 },
      { id: 'preferredDropoff', type: 'select', label: 'Preferred Drop-off Time', name: 'preferredDropoff', required: true, options: ['Morning', 'Afternoon', 'Evening'], order: 6 },
      { id: 'problemDescription', type: 'textarea', label: 'Describe the Problem', name: 'problemDescription', placeholder: '', required: true, order: 7 },
    ],
    defaultTheme: {
      primaryColor: '#dc2626',
      backgroundColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#dddddd',
      borderRadius: '8px',
      buttonColor: '#dc2626',
      buttonTextColor: '#ffffff',
    },
    aiKnowledgeBase: `You are the intake assistant for an auto repair shop. Your job is to qualify incoming service requests before a human follows up.

When a new lead comes in:
- Confirm the vehicle year, make, and model, and the service needed (diagnostics, brakes, oil change and maintenance, engine, transmission, tires, or other).
- Ask whether the vehicle is currently drivable, since that affects how urgently it needs to be seen and whether a tow is needed.
- Ask about their preferred drop-off time (morning, afternoon, or evening).
- Be friendly and practical. Car trouble is often stressful and can affect someone's ability to get to work.
- Do not quote exact prices unless pricing information has been provided in this knowledge base. If unsure, say a technician will confirm pricing after inspecting the vehicle.
- If the lead asks about scheduling, offer to have someone follow up, and only share a booking link if one has been set up for this form.
- Keep replies short and to the point.`,
    followUpDefaults: { followUpEnabled: true, followUpDelayDays: 1, maxFollowUps: 2 },
  },
];

/**
 * Get template by ID
 */
export function getTemplate(templateId) {
  return FORM_TEMPLATES.find(t => t.id === templateId);
}

/**
 * Get all templates
 */
export function getAllTemplates() {
  return FORM_TEMPLATES;
}

/**
 * Get templates grouped by category. Templates without a category are
 * treated as 'general' so older/custom template objects keep working.
 */
export function getTemplatesByCategory() {
  const general = [];
  const industry = [];
  for (const template of FORM_TEMPLATES) {
    if (template.category === 'industry') {
      industry.push(template);
    } else {
      general.push(template);
    }
  }
  return { general, industry };
}

/**
 * Get layout by ID
 */
export function getLayout(layoutId) {
  return LAYOUT_OPTIONS.find(l => l.id === layoutId);
}

/** Display label for a row in Accordion/Tabbed layouts (and Multi-step when set). */
export function getRowSectionLabel(row, index) {
  const t = row?.title?.trim();
  return t || `Section ${index + 1}`;
}

/**
 * Get all layouts
 */
export function getAllLayouts() {
  return LAYOUT_OPTIONS;
}

const DEFAULT_BLANK_THEME = {
  primaryColor: '#171A0D',
  backgroundColor: '#ffffff',
  textColor: '#333333',
  borderColor: '#dddddd',
  borderRadius: '8px',
  buttonColor: '#171A0D',
  buttonTextColor: '#ffffff',
};

const DEFAULT_BLANK_FIELDS = [
  { id: 'name', type: 'text', label: 'Full Name', name: 'name', placeholder: 'Your name', required: true, order: 0 },
  { id: 'email', type: 'email', label: 'Email Address', name: 'email', placeholder: 'you@example.com', required: true, order: 1 },
  { id: 'message', type: 'textarea', label: 'Message', name: 'message', placeholder: 'How can we help you?', required: true, order: 2 },
];

const FULL_WIDTH_FIELD_TYPES = new Set(['textarea']);
const SHORT_FIELD_TYPES = new Set([
  'text', 'email', 'phone', 'tel', 'number', 'select', 'date', 'radio', 'checkbox',
]);

/**
 * Build a sensible multi-row layout from a field list. Deterministic: sorts by
 * `order`, gives textarea its own row, pairs short fields up to 2 per row.
 */
export function buildSmartRows(fields) {
  const sorted = [...(fields || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const rows = [];
  let pendingShort = [];
  let rowNum = 1;

  const flushPending = () => {
    if (pendingShort.length === 0) return;
    rows.push({ id: `row-${rowNum}`, fieldIds: pendingShort.map((f) => f.id) });
    rowNum += 1;
    pendingShort = [];
  };

  for (const field of sorted) {
    if (FULL_WIDTH_FIELD_TYPES.has(field.type)) {
      flushPending();
      rows.push({ id: `row-${rowNum}`, fieldIds: [field.id] });
      rowNum += 1;
    } else if (SHORT_FIELD_TYPES.has(field.type)) {
      pendingShort.push(field);
      if (pendingShort.length === 2) flushPending();
    } else {
      flushPending();
      rows.push({ id: `row-${rowNum}`, fieldIds: [field.id] });
      rowNum += 1;
    }
  }

  flushPending();
  return rows;
}

/**
 * Build the Firestore-ready `forms` document data for a new form given a
 * chosen template, layout, and theme. Pulled out of FormBuilderList so the
 * shape is unit-testable and template-agnostic (works with or without
 * `category`/`aiKnowledgeBase` on the template).
 */
export function buildNewFormData({ template, layout, theme: customTheme, workspaceId, businessName, createdBy }) {
  let theme = customTheme || (template ? template.defaultTheme : DEFAULT_BLANK_THEME);

  if (layout && layout.styles) {
    theme = {
      ...theme,
      layout: layout.styles.layout,
      fieldStyle: layout.styles.fieldStyle,
      buttonStyle: layout.styles.buttonStyle,
      spacing: layout.styles.spacing,
    };
  }

  const fields = template ? template.fields : DEFAULT_BLANK_FIELDS;

  return {
    workspaceId,
    createdBy,
    name: template ? template.name : 'New Form',
    fields,
    rows: buildSmartRows(fields),
    settings: {
      spamDetection: true,
      spamRedirect: true,
      aiTesting: false,
      autoReply: false,
      scheduleMeetings: false,
      trackingPlatforms: [],
      autoUpdateTracking: false,
      // Reply "Shown as" name — never form.name. See src/utils/replyIdentity.js.
      businessName: businessName || null,
    },
    theme,
    layout: layout ? layout.id : 'modern',
    embedCode: '',
    trackingPixels: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}
